import styled, { css } from 'styled-components';

const BUTTON_HEIGHT = '36px';
const BUTTON_RADIUS = '8px';

export const UsersCardContainer = styled.section`
  background: #fff;
  border: 1px solid rgba(34, 36, 38, 0.1);
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(34, 36, 38, 0.06);
  padding: 1.5rem;
  min-height: 420px;
  position: relative;
`;

export const UsersCardHeader = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

export const UsersCardTitleGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;
`;

export const UsersCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(89, 36, 155, 0.08);
  color: ${props => props.theme.main.color};
  flex-shrink: 0;

  .icon {
    margin: 0;
    font-size: 1.1rem;
  }
`;

export const UsersCardTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #2b313c;
  line-height: 1.3;
`;

export const UsersCardSubtitle = styled.p`
  margin: 0.2rem 0 0;
  font-size: 0.85rem;
  color: #767676;
  line-height: 1.4;
`;

export const UsersCardActions = styled.div`
  flex-shrink: 0;

  @media (max-width: 576px) {
    width: 100%;
  }
`;

export const UsersList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const UserRowContainer = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 0;
  border-bottom: 1px solid rgba(34, 36, 38, 0.08);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }

  @media (max-width: 576px) {
    flex-wrap: wrap;
    align-items: flex-start;
  }
`;

export const UserRowMain = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
`;

export const UserAvatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f0edf5;
  color: ${props => props.theme.main.color};
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
`;

export const UserInfo = styled.div`
  min-width: 0;
`;

export const UserName = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #2b313c;
  line-height: 1.3;
`;

export const UserMeta = styled.div`
  font-size: 0.82rem;
  color: #767676;
  margin-top: 0.15rem;
  line-height: 1.35;
  word-break: break-word;
`;

export const UserRowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 576px) {
    width: 100%;
    padding-left: calc(40px + 0.75rem);
    justify-content: space-between;
  }
`;

export const StatusBadgeWrapper = styled.span<{ $variant: 'active' | 'pending' | 'inactive' | 'default' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  line-height: 1.2;

  .icon {
    margin: 0;
    font-size: 0.75rem;
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 'active':
        return css`
          background: #e8f7ec;
          color: #1e7a34;
        `;
      case 'pending':
        return css`
          background: #fff4e0;
          color: #b37400;
        `;
      case 'inactive':
        return css`
          background: #f1f1f1;
          color: #6b6b6b;
        `;
      default:
        return css`
          background: #f1f1f1;
          color: #6b6b6b;
        `;
    }
  }}
`;

const actionButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: ${BUTTON_HEIGHT};
  padding: 0 1rem;
  border-radius: ${BUTTON_RADIUS};
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease,
    opacity 0.15s ease;
  white-space: nowrap;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.main.color};
    outline-offset: 2px;
  }
`;

export const PrimaryActionButton = styled.button`
  ${actionButtonBase}
  background: ${props => props.theme.main.color};
  border: 1px solid ${props => props.theme.main.color};
  color: #fff;

  &:hover:not(:disabled) {
    background: #4a1d82;
    border-color: #4a1d82;
  }
`;

export const SecondaryActionButton = styled.button`
  ${actionButtonBase}
  background: #fff;
  border: 1px solid ${props => props.theme.main.color};
  color: ${props => props.theme.main.color};

  &:hover:not(:disabled) {
    background: rgba(89, 36, 155, 0.06);
  }

  @media (max-width: 576px) {
    width: 100%;
  }
`;

export const GhostDestructiveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 0.2rem 0.4rem;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #db2828;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(219, 40, 40, 0.08);
    color: #b21e1e;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #db2828;
    outline-offset: 2px;
  }
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2.5rem 1rem;
  color: #767676;
`;

export const EmptyStateIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f4f4f5;
  color: #9a9a9a;
  margin-bottom: 0.75rem;

  .icon {
    margin: 0;
    font-size: 1.25rem;
  }
`;

export const EmptyStateText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  max-width: 280px;
`;

export const PaginationWrapper = styled.div`
  margin-top: 1.25rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(34, 36, 38, 0.08);
`;
